/**
 * №29 (S2) — the TABLE SUBJECT's head (mockup B: "the table is the stage").
 *
 * The probe's crumb for a collection is `Matters / Limitation sweep`, with the
 * surface's verbs on the right and the table filling everything below. What every
 * table surface in the App Shell had instead was a `.pagehead` INSIDE the stage —
 * an `<h1>`, a subline and a primary button — sitting directly under a stage head
 * that was already naming the same Place. Two titles, one surface.
 *
 * So the title and the verbs move up, and this component is the move. It is one
 * component rather than eight edits-in-place for the reason R1 exists: eight surfaces
 * hand-declaring their own stage head is eight chances for them to disagree about
 * what a table's head looks like.
 *
 * ## The one piece of judgement in here
 *
 * The stage head already renders the ANCHOR, which for most Places is the same word
 * as the page's title ("Matters" / "Matters"). Declaring it again as the working
 * title would put it on screen twice with a slash between. So the title is declared
 * only when it differs from the anchor — which is exactly the case where it is worth
 * saying: `Register entries / Register of Powers & Estates`, `Contacts / Clients`.
 *
 * The SUBLINE stays on the stage. It is the table's own caption ("24 matters · one
 * Contacts master, many lenses"), it changes with the data, and the probe keeps that
 * kind of line with the table (`Computing 2 of 24 rows`), not in the crumb.
 */
import type { ReactNode } from "react";
import { useAppScope } from "../../hooks/use-app-scope";
import { StageActions, useStageHead } from "./stage-head";

export function TableSubjectHead({
  title,
  sub,
  actions,
}: {
  /** The surface's own name. Suppressed in the head when it IS the anchor. */
  title: string;
  /** The table's caption — row counts, what the lens is. Stays on the stage. */
  sub?: ReactNode;
  /** The surface's primary verbs — "New matter", "＋ Vendor invoice". */
  actions?: ReactNode;
}) {
  const { chip } = useAppScope();
  const distinct = title.trim() && title.trim().toLowerCase() !== chip.trim().toLowerCase();
  useStageHead({ title: distinct ? title : null });
  return (
    <>
      {actions && <StageActions>{actions}</StageActions>}
      {sub ? <div className="tablecap" data-testid="table-caption">{sub}</div> : null}
    </>
  );
}
